import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import MultiComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/multi-combo-box';
import Label from 'atom-components/label';
import { friendlyMode } from 'helpers/domain-specific/response-mode.helpers';
import { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';

const fieldLabel = 'Mode(s)';
const fieldName = 'modes';

type Props = {
  triggerFormSubmission: () => void;
};

export default function ModeFilter({ triggerFormSubmission }: Props): ReactElement {
  const { control, setValue } = useFormContext();

  // "Media" modes will never show up in the data tables, so they shouldn't be selectable:
  const selectableModes: ComboBoxItem[] = Object.values(TestInstanceResponseInputType)
    .filter((mode) => mode !== TestInstanceResponseInputType.Media)
    .map((mode) => ({
      id: mode.toString(),
      name: friendlyMode(mode)
    }));

  return (
    <div>
      <Label htmlFor={fieldName}>{fieldLabel}</Label>
      <MultiComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableModes}
        clearable
        onChange={(items): void => {
          const modes = items.map((item) => item.id);
          setValue(fieldName, modes, {
            shouldValidate: true,
            shouldTouch: true
          });
          triggerFormSubmission();
        }}
      />
    </div>
  );
}

export function ModeAppliedFilter({ triggerFormSubmission }): ReactElement | null {
  const { watch, setValue } = useFormContext();
  const modes: TestInstanceResponseInputType[] = watch(fieldName);

  if (modes.length === 0) return null;

  return (
    <AppliedFilters.Filter name={fieldLabel}>
      {modes.map(
        (mode, index): ReactElement => (
          <AppliedFilters.FilterItem
            key={mode}
            onClick={(): void => {
              const modesCopy = [...modes]; // Todo: Not needed once we change to toSpliced
              modesCopy.splice(index, 1);
              setValue(fieldName, modesCopy);
              triggerFormSubmission();
            }}
          >
            {friendlyMode(mode)}
          </AppliedFilters.FilterItem>
        )
      )}
    </AppliedFilters.Filter>
  );
}
